import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import tw from "tailwind.macro";
import { Dropdown } from "components/components/atoms";
import useBreakpoint from "utils/hooks/useBreakpoint";
import ChevronDown from "components/icons/ChevronDown";
import { useSpring, animated } from "react-spring";
import cx from "classnames";

const Container = styled.div`
  ${({ isMobile }) => (isMobile ? `width: 100%;` : `width: 10rem;`)}
`;

const FilterContainer = styled.div`
  ${({ isMobile }) => (isMobile ? `width: 100%;` : `width: 8.75rem;`)}
`;

const DropDownButton = styled.button`
  ${({ isMobile }) => (isMobile ? `height: 3.125rem;` : `height: 2.5rem;`)}
  ${tw`flex w-full items-center text-left justify-between px-4 cursor-pointer rounded-lg border border-white`}
  &:hover {
    ${tw`bg-white text-action-blue`}
  }
  ${({ active }) => active && tw`bg-white text-action-blue`}
`;

const LabelContainer = styled.div.attrs({
  className: "h-full flex flex-col justify-center text-sm mr-4",
})`
  width: 87%;
  line-height: 1.125rem;
`;

const DropDownContent = styled(animated.div)`
  ${tw`bg-white text-action-blue hidden flex-col overflow-y-scroll`};
  ${({ active }) => active && tw`flex mt-2 p-3 rounded-lg`}
  position: relative;
  max-height: 15.938rem;
`;
const Button = styled.button`
  ${tw`inline-block mb-2 text-left`};
  &:nth-last-child(1) {
    margin-bottom: 0rem;
  }
  min-height: 1.125rem;
`;
const formatPrice = (value) =>
  value >= 1000000 ? `${value / 1000000}M` : `${value / 1000}K`;

const getNumericValue = (value) => {
  if (!value) return 0;
  return value[value.length - 1] &&
    value[value.length - 1].toLowerCase() === "k"
    ? value.slice(0, -1) * 1000
    : value.slice(0, -1) * 1000000;
};

const getOptions = (min, max) => {
  const minPrice = getNumericValue(min);
  const maxPrice = getNumericValue(max);

  const options = [minPrice];
  let last = minPrice;

  while (last < maxPrice) {
    if (last < 1000000) {
      options.push(last + 50000);
    } else {
      options.push(last + 250000);
    }
    last = options[options.length - 1];
  }
  return options;
};

const PriceDropdown = (props) => {
  const {
    priceFilter,
    handleFilterChange,
    priceRangeMin,
    priceRangeMax,
    active: activeProp,
    setActive: setActiveProp,
    clearPriceFilters,
    availableOptions = {},
  } = props;

  const ref = useRef();
  const [activeState, setActiveState] = useState(false);

  const active = activeProp || activeState;
  const setActive = setActiveProp || setActiveState;

  const [minActive, setMinActive] = useState(false);
  const [maxActive, setMaxActive] = useState(false);
  const options = getOptions(priceRangeMin, priceRangeMax);

  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);

  const dropdownMinStyle = useSpring({
    height: minActive ? "auto" : 0,
  });

  const dropdownMaxStyle = useSpring({
    height: maxActive ? "auto" : 0,
  });

  let minPrice;
  let maxPrice;

  if (priceFilter) {
    minPrice = priceFilter.min;
    maxPrice = priceFilter.max;
  }

  const [lastPressedButton, setLastPressedButton] = useState("");
  const announcementRef = useRef(null);
 
  useEffect(() => {
    if (lastPressedButton) {
      announcementRef.current.textContent = `${lastPressedButton} pressed`;
    }
  }, [lastPressedButton]);

  return (
    <Container
      isMobile={isMobile}
      className="relative mb-5 md:mb-0"
      onBlur={() => {
        setTimeout(() => {
          const containsFocusedElement = ref.current.contains(
            document.activeElement
          );
          if (!containsFocusedElement) {
            setActive(false);
          }
        }, 0);
      }}
      ref={ref}
    >
      <Dropdown
        label={
          minPrice || maxPrice
            ? minPrice && maxPrice
              ? `$${formatPrice(minPrice)} - $${formatPrice(maxPrice)}`
              : minPrice
              ? `$${formatPrice(minPrice)} - No max`
              : `No min - $${formatPrice(maxPrice)}`
            : "Select Price"
        }
        active={active}
        setActive={setActive}
        outlined
        textLightBlue
        small={!isMobile}
        dropdownDescription={`Dropdown price filter: ${
          minPrice || maxPrice
            ? `Selected price range is ${minPrice ? `$${formatPrice(minPrice)}` : "No Min"} to ${maxPrice ? `$${formatPrice(maxPrice)}` : "No Max"}`
            : "No price range selected."
        }`}
        withSelectClick={true}
      >
        <div className="md:flex w-full h-full px-2 my-3 md:my-0">
          <FilterContainer isMobile={isMobile} className="mb-5 md:mb-0 md:mr-5">
            <DropDownButton
              role="dropdown"
              className="transition transition-all duration-300"
              active={minActive}
              onClick={() => {
                setMinActive(!minActive);
                setMaxActive(false);
              }}
              aria-label={minPrice
                ? `Dropdown min price, selected min price is ${minPrice ? `$ ${formatPrice(minPrice)}` : "No Min"}` 
                : "Dropdown min price"}
            >
              <LabelContainer>
                <span className="truncate">
                  {minPrice ? `$ ${formatPrice(minPrice)}` : "$ No min"}
                </span>
              </LabelContainer>
              <ChevronDown className="w-10-mattamy" />
            </DropDownButton>
            {minActive && (
              <DropDownContent style={dropdownMinStyle} active={minActive}>
                <Button
                  onClick={(e) => {
                    handleFilterChange("price", {
                      min: 0,
                      max: maxPrice || undefined,
                    });
                    setLastPressedButton("Any Price Button");
                    setMinActive(false);
                  }}
                >
                  Any Price
                </Button>
                {options.map((option) => (
                  <Button
                    className={cx("cursor-pointer select-none", {
                      /*"cursor-pointer select-none":
                        option > availableOptions.minPrice - 50000 &&
                        option < availableOptions.maxPrice,
                      "pointer-events-none text-mattamy-gray-light": !(
                        option > availableOptions.minPrice - 50000 &&
                        option < availableOptions.maxPrice
                      ),*/
                    })}
                    key={option.toString()}
                    onClick={(e) => {
                      handleFilterChange("price", {
                        min: option,
                        max: maxPrice || undefined,
                      });
                      setLastPressedButton(`$ ${formatPrice(option)}`);
                      setMinActive(false);
                    }}
                  >
                    $ {formatPrice(option)}
                  </Button>
                ))}
              </DropDownContent>
            )}
          </FilterContainer>
          <FilterContainer isMobile={isMobile}>
            <DropDownButton
              role="dropdown"
              className="transition transition-all duration-300"
              active={maxActive}
              onClick={() => {
                setMaxActive(!maxActive);
                setMinActive(false);
              }}
              aria-label={maxPrice
                ? `Dropdown max price, selected max price is ${maxPrice ? `$ ${formatPrice(maxPrice)}` : "No Max"}` 
                : "Dropdown max price"}
            >
              <LabelContainer>
                <span className="truncate">
                  {maxPrice ? `$ ${formatPrice(maxPrice)}` : "$ No Max"}
                </span>
              </LabelContainer>
              <ChevronDown className="w-10-mattamy" />
            </DropDownButton>
            {maxActive && (
              <DropDownContent style={dropdownMaxStyle} active={maxActive}>
                <Button
                  onClick={(e) => {
                    handleFilterChange("price", {
                      min: minPrice || 0,
                      max: undefined,
                    });
                    setLastPressedButton("Any Price button");
                    setMaxActive(false);
                  }}
                >
                  Any Price
                </Button>
                {options.map((option) => (
                  <Button
                    className={cx("cursor-pointer select-none", {
                      /*"cursor-pointer select-none":
                        option >
                          ((priceFilter && priceFilter.min) ||
                            availableOptions.minPrice) &&
                        option < availableOptions.maxPrice + 50000,
                      "pointer-events-none text-mattamy-gray-light": !(
                        option >
                          ((priceFilter && priceFilter.min) ||
                            availableOptions.minPrice) &&
                        option < availableOptions.maxPrice + 50000
                      ),*/
                    })}
                    key={option}
                    onClick={(e) => {
                      handleFilterChange("price", {
                        min: minPrice || 0,
                        max: option,
                      });
                      setLastPressedButton(`$ ${formatPrice(option)}`);
                      setMaxActive(false);
                    }}
                  >
                    $ {formatPrice(option)}
                  </Button>
                ))}
              </DropDownContent>
            )}
          </FilterContainer>
        </div>
        {/* <div className="md:flex w-full h-full px-2 my-3 md:my-3">
          <button
            className="flex items-center justify-center border border-white rounded-lg select-none cursor-pointer w-full h-10 md:hover:bg-white md:hover:text-action-blue"
            onClick={clearPriceFilters}
          >
            Clear
          </button>
        </div> */}
      </Dropdown>
      <div
        aria-live="polite"
        aria-atomic="true"
        ref={announcementRef}
        style={{ position: "absolute", left: "-9999px" }}
      />
    </Container>
  );
};

export default PriceDropdown;
